// ContactPage.js — how to reach the site operator: what to send for each
// kind of enquiry, then email and X buttons. No backend form, so there's
// nothing to spam.

import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import XIcon from "@mui/icons-material/X";
import SEO from "./components/SEO";
import { InternalLink, P, PageHeader, ProsePage } from "./components/Prose";
import { BRAND } from "./theme";

const CONTACT_EMAIL = "mason23dev@gmail.com";

const TOPICS = [
  {
    title: "Data corrections",
    body: "If a result looks wrong, please include the match, the date and the correct score. Every report is checked against independent records before the data is changed.",
  },
  {
    title: "Feedback and ideas",
    body: "Suggestions for new features, records or articles are always welcome, as are reports of anything that doesn't work as expected.",
  },
  {
    title: "Press and media",
    body: "For interviews, data requests or permission to use images from the site, please email with details of your publication and deadline.",
  },
];

export default function ContactPage() {
  return (
    <>
      <SEO
        title="Contact"
        description="Contact Premier League Scorigami, run by Mason Maier, about data corrections, feedback, partnerships or press enquiries."
        path="/contact"
      />
      <ProsePage>
        <PageHeader
          eyebrow="Contact"
          title="Get in touch"
          lede="Premier League Scorigami is an independent project run by Mason Maier. Corrections, feedback and press enquiries are all welcome."
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
            mb: 4,
          }}
        >
          {TOPICS.map((t) => (
            <Box
              key={t.title}
              sx={{
                p: 2.5,
                borderRadius: 3,
                background: "linear-gradient(180deg, rgba(25,25,32,0.8) 0%, rgba(18,18,24,0.8) 100%)",
                border: `1px solid ${BRAND.divider}`,
              }}
            >
              <Typography component="h2" sx={{ color: "#fff", fontWeight: 700, fontSize: 16, mb: 1 }}>
                {t.title}
              </Typography>
              <Typography sx={{ color: BRAND.textSecondary, fontSize: 14.5, lineHeight: 1.7 }}>{t.body}</Typography>
            </Box>
          ))}
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 4 }}>
          <Button
            startIcon={<MailOutlineIcon />}
            href={`mailto:${CONTACT_EMAIL}`}
            variant="contained"
            sx={{
              background: BRAND.gradient,
              color: "#fff",
              px: 3,
              py: 1.25,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            {CONTACT_EMAIL}
          </Button>
          <Button
            startIcon={<XIcon sx={{ fontSize: 18 }} />}
            href="https://x.com/pl_gami"
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            sx={{
              borderColor: "rgba(255,255,255,0.18)",
              color: "#e6e8ee",
              px: 3,
              py: 1.25,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { borderColor: BRAND.cyan, backgroundColor: "rgba(57,208,255,0.06)" },
            }}
          >
            Message @pl_gami on X
          </Button>
        </Stack>

        <P>
          Premier League Scorigami is not affiliated with the Premier League or any club. Answers to common questions are
          on the <InternalLink to="/faq">FAQ</InternalLink>, and more about the project is on the{" "}
          <InternalLink to="/about">About page</InternalLink>.
        </P>
      </ProsePage>
    </>
  );
}
